import { Box, Button, Divider, HStack, Icon, Image, Spinner, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, Text, Tooltip, useDisclosure, VStack } from "@chakra-ui/react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Slider from "react-slick";
import { createClickLog, createMedicineEntryLog, getMedicine, getMedicinesByKeywords } from "../../api";
import LogInOnly from "../../components/Auth/LogInOnly";
import MyIcon from "../../components/Icon/MyIcon";
import TooltipIcon from "../../components/Icon/TooltipIcon";
import Template from "../../components/Layout/Template";
import HMedicineListItem from "../../components/Medicine/HMedicineListItem";
import SimilarModal from "../../components/Modals/SimilarModal";
import Alert, { useAlert } from "../../components/Alert/Alert";
import { colors } from "../../styles";
import { IMedicine, IMedicineDetail, IMedicinesByKeywordsList } from "../../types";
import { formatPrice, noteTagToBackgroundColor, noteTagToLabelColor, noteToTags } from "../../utils";
import { useAuthStore } from "../../store";
import useMe from "../../hooks/useMe";
import { ViewIcon } from "@chakra-ui/icons"

export default function Medicine() {

    /* params */

    const { medicineId } = useParams();

    /* useMe */

    const { meData, meLoading } = useMe();

    const userType = useMemo(() => meData?.user.type ?? "", [meData]);
    const userStatus = useMemo(() => meData?.user.status ?? "", [meData]);
    
    const isOwnerAndActive = useMemo(() => userType === "OWNER" && userStatus === "ACTIVE", [userType, userStatus]);

    /* useAuth */

    const { isGuest } = useAuthStore();
    const { showAlert, alertProps } = useAlert();

    const isRestrictedUser = useMemo(() => isGuest || userType === "MEMBER", [isGuest, userType]);
    const restrictedAlertMessage = useMemo(
        () => (userType === "MEMBER" ? "승인 후 이용할 수 있습니다" : "회원가입 후 이용할 수 있습니다."),
        [userType]
    );

    /* query */

    const { data: medicineData, isLoading: isMedicineDataLoading } = useQuery<IMedicineDetail>({
        queryKey: ['medicine', medicineId],
        queryFn: () => getMedicine({ medicineId: medicineId! }),
        enabled: !!medicineId,
        // 조회수는 서버가 `getMedicine` 호출 시 증가시키는 형태로 보이므로,
        // 포커스 변경/리마운트 등에 의해 불필요하게 재요청되지 않게 한다.
        staleTime: Infinity,
        gcTime: 1000 * 60 * 60 * 24, // 24시간 동안 캐시 유지
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
    });

    const avoidNullString = (value: string | undefined) => {
        if (value === "NULL" || value === undefined || value === null) return "-";
        return value;
    };

    /* useState */

    const [packImage, setPackImage] = useState<string[]>([]);
    const [unitPriceArray, setUnitPriceArray] = useState<any[]>([]);


    /* medicineName */

    const medicineName = medicineData ? avoidNullString(medicineData.medicine.nameKor).split('(')[0].trim() : "";

    /* tags */

    const tags: string[] = noteToTags(medicineData?.medicine.note);

    /* company mall */

    const mallUrl = medicineData?.medicine.company?.mall;
    const mallHref = useMemo(() => {
        if (!mallUrl) return null;
        const trimmed = mallUrl.trim();
        if (!trimmed) return null;
        if (/^https?:\/\//i.test(trimmed)) return trimmed;
        return `https://${trimmed}`;
    }, [mallUrl]);

    /* useEffect */

    useEffect(() => {
        if(medicineData) {

            ////////////////////////////////////////////////////////////////////// 이미지 변환

            const urlString = medicineData.medicine.packImage;
        
            if(urlString) {
                const urlArray = urlString.split(';');
                setPackImage(urlArray);
            }

            ////////////////////////////////////////////////////////////////////// 수량할인 가격 계산

            if(medicineData.medicine.unitPrice) {

                const result = medicineData.medicine.unitPrice.split(";").map((item, index, array) => {
                    const [count, price] = item.split("|").map(Number);
                        // 첫 번째 요소의 가격을 기준으로 할인율 계산
                    const basePrice = Number(medicineData.medicine.basePrice);
                    const discount = Math.round(((basePrice - price) / basePrice) * 1000) / 10;
    
                    return { count, price, discount };
                })
    
                if(result) {
                    setUnitPriceArray(result);
                }

            }
        }

    }, [medicineData]);

    /* selectedKeywords */

    const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);

    const changeSelectedKeywords = (keywords: string[]) => {

        if(isRestrictedUser) {   
            showAlert(restrictedAlertMessage);
            return;
        }

        setSelectedKeywords(keywords);
    }

    /* 키워드를 새로 선택할 때만 기록한다. 해제와 제한 사용자 클릭은 남기지 않는다. */

    const handleKeywordButtonClick = (keywordId: string, keyword: string) => {

        const isSelected = selectedKeywords?.includes(keyword);

        if (!isSelected && !isRestrictedUser && medicineId) {
            createClickLog({
                type: "KEYWORD",
                surface: "MEDICINE",
                medicineId,
                keywordId,
                keyword,
            });
        }

        changeSelectedKeywords(isSelected ? selectedKeywords?.filter((selectedKeyword) => selectedKeyword !== keyword) : [...(selectedKeywords || []), keyword]);
    }

    useEffect(() => {
        setSelectedKeywords([]);
    }, [medicineId]);

    /* query */

    const size = 16;

    /* useInfiniteQuery */

    const { data: medicinesByKeywordsData, isLoading: isMedicinesByKeywordsDataLoading, fetchNextPage: fetchNextMedicinesByKeywords, hasNextPage: hasNextMedicinesByKeywordsPage, isFetchingNextPage: isFetchingNextMedicinesByKeywordsPage } = useInfiniteQuery<IMedicinesByKeywordsList>({

        // 쿼리 키
        queryKey: ['getMedicinesByKeywords', medicineId, selectedKeywords],

        // 쿼리 함수 - pageParam은 useInfiniteQuery의 파라미터로 전달되는 값 중 페이지 번호를 의미
        queryFn: ({ pageParam }) => {

            // pageParam은 unknown 타입이므로 명시적으로 number 타입으로 변환
            const page = pageParam as number;
            
            return getMedicinesByKeywords({ 
                medicineId: medicineId!,
                keywords: selectedKeywords,
                page, 
                size, 
            })
        },

        // 초기 페이지 파라미터 : 1 또는 0 사용
        initialPageParam: 1,

        // 쿼리 활성화 조건
        enabled: !!medicineId && selectedKeywords.length > 0,

        // 다음 페이지 파라미터 결정
        getNextPageParam: (lastPageData: IMedicinesByKeywordsList) => {
            return lastPageData.hasNextPage ? lastPageData.currentPage + 1 : undefined;
        }
    });


    /* carousel settings */
    
    const settings = {
        dots: true,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        // autoplay: true,
        // autoplaySpeed: 2000,
    };

    /* useState */

    const { isOpen: isOpenSimilarModal, onOpen: onOpenSimilarModal, onClose: onCloseSimilarModal } = useDisclosure();

    /* 동일성분 모달을 여는 클릭만 기록한다 */

    const handleOpenSimilarModal = () => {

        onOpenSimilarModal();

        if (!medicineData?.medicine.id) return;

        createClickLog({
            type: "SIMILAR_MODAL",
            surface: "MEDICINE",
            medicineId: medicineData.medicine.id,
        });
    }

    /* rendering */

    return (
        <LogInOnly>
            <Template>
                {isMedicineDataLoading ? 
                    <VStack justifyContent={"center"} alignItems={"center"} width={"100%"} padding={10}>
                        <Spinner color={colors.blue} />
                    </VStack>
                    :
                    <Box marginTop={5}>
                        <Slider {...settings}>
                            {packImage.map((image, index) => (
                                <Image
                                    key={index}
                                    src={image}
                                    width={{ base: 240, sm: 240, md: 280, lg: 280, xl: 320 }}
                                    height={{ base: 240, sm: 240, md: 280, lg: 280, xl: 320 }}
                                    objectFit="contain"
                                />
                            ))}
                        </Slider>
                    </Box>
                }

                {medicineData &&
                    <HStack justifyContent={"space-between"} alignItems={"center"} padding={5} width={"100%"}>
                        <VStack alignItems={"flex-start"}>
                            <Text fontSize={14} fontWeight={600} color={colors.g3}>{medicineData?.medicine.seller ?? "-"}</Text>
                            <Text fontSize={22} fontWeight={700} color={colors.g1}>{medicineName}</Text>
                            <HStack justifyContent={"flex-start"} alignItems={"center"} spacing={1}>
                                <MyIcon src="/svg/capsule-filled.svg" alt="medicine" width={16} height={16} />
                                <Text fontSize={16} fontWeight={700} color={colors.g1}>{medicineData?.medicine.packageUnit ?? "-"}</Text>
                            </HStack>
                            <HStack 
                                justifyContent={"flex-start"} 
                                alignItems={"center"} 
                                spacing={1} 
                                marginTop={2}

                                onClick={handleOpenSimilarModal}
                                cursor={"pointer"}
                            >
                                <Text fontSize={14} fontWeight={500} textDecoration={"underline"} color={colors.g1}>{medicineData?.medicine.ingredientInfo ?? "-"}</Text>
                                <MyIcon src={"/svg/window.svg"} alt="external link" width={12} height={12} />
                            </HStack>

                            <SimilarModal isOpen={isOpenSimilarModal} onClose={onCloseSimilarModal} medicineId={medicineData.medicine.id} surface="MEDICINE" />

                            {tags.length > 0 && (
                                <HStack
                                    justifyContent={"flex-start"}
                                    alignItems={"center"}
                                    spacing={2}
                                    marginY={4}
                                    flexWrap={"wrap"}
                                >
                                    {tags.map((tag, index) => (
                                        <Tag
                                            key={index}
                                            backgroundColor={noteTagToBackgroundColor(tag)}
                                            color={noteTagToLabelColor(tag)}
                                            size="sm"
                                            borderRadius="full"
                                            padding={2}
                                            fontSize={10}
                                        >
                                            {tag}
                                        </Tag>
                                    ))}
                                </HStack>
                            )}
                        </VStack>
                    </HStack>
                }

                {/* 키워드 목록 */}

                <HStack width={"100%"} justifyContent={"flex-start"} spacing={2} flexWrap={"wrap"} paddingX={5}>
                    {medicineData?.medicine.connections?.filter((connection) => connection.weight >= 10).map((connection, index) => (
                        <Button 
                            key={index} 
                            onClick={() => handleKeywordButtonClick(connection.keyword.id, connection.keyword.keyword)}
                            
                            size={"md"} 
                            backgroundColor={selectedKeywords?.includes(connection.keyword.keyword) ? colors.blue : colors.white}
                            borderWidth={2}
                            // borderRadius={12}
                            borderColor={selectedKeywords?.includes(connection.keyword.keyword) ? colors.blue : colors.light}
                            color={selectedKeywords?.includes(connection.keyword.keyword) ? colors.white : colors.black} 
                            _hover={{ 
                                backgroundColor: selectedKeywords?.includes(connection.keyword.keyword) ? colors.blue : colors.white, 
                                color: selectedKeywords?.includes(connection.keyword.keyword) ? colors.white : colors.blue 
                            }}
                        >
                            {connection.keyword.keyword}
                        </Button>
                    ))}
                </HStack>

                {/* 키워드에 따른 제품 추천 */}

                {selectedKeywords.length > 0 &&
                    <Stack
                        width={"100%"}
                        marginTop={10}
                        // Fade in animation for the whole section
                        animation="fadeInUp 0.6s ease"
                        sx={{
                            '@keyframes fadeInUp': {
                                from: {
                                    opacity: 0,
                                    transform: 'translate3d(0, 30px, 0)'
                                },
                                to: {
                                    opacity: 1,
                                    transform: 'none'
                                }
                            }
                        }}
                    >
                        <HStack width={"100%"} justifyContent={"flex-start"} spacing={2} flexWrap={"wrap"}>
                            {selectedKeywords.map((keyword, index) => (
                                <Text
                                    key={index}
                                    fontSize={14}
                                    fontWeight={700}
                                    color={colors.blue}
                                    // 부드럽게 등장
                                    animation="fadeKeyword 0.5s"
                                    sx={{
                                        animationDelay: `${index * 0.07 + 0.1}s`,
                                        animationFillMode: "backwards",
                                        '@keyframes fadeKeyword': {
                                            from: {
                                                opacity: 0,
                                                transform: 'translateY(10px)'
                                            },
                                            to: {
                                                opacity: 1,
                                                transform: 'none'
                                            }
                                        }
                                    }}
                                >{`#${keyword}`}</Text>
                            ))}
                        </HStack>

                        {/* 키워드에 따른 제품 추천 목록 */}

                        <HStack
                            overflowX={"scroll"}
                            width={"100%"}
                            minHeight={"220px"}
                            spacing={4}
                            flexWrap={"nowrap"}
                            paddingY={5}
                        >

                            {isMedicinesByKeywordsDataLoading && (
                                <Spinner color={colors.blue} />
                            )}

                            {(!isMedicinesByKeywordsDataLoading && medicinesByKeywordsData && medicinesByKeywordsData.pages.flatMap((page) => page.medicines).length > 0) && (
                                medicinesByKeywordsData.pages.flatMap((page) => page.medicines).map((medicine, index) => (
                                        <HMedicineListItem
                                            key={medicine.id}
                                            index={index}
                                            id={medicine.id ?? ""}
                                            medicineImage={medicine.packImage ?? ""}
                                            seller={medicine.seller ?? ""}
                                            nameKor={medicine.nameKor ?? ""}
                                            packageUnit={medicine.packageUnit ?? ""}
                                            basePrice={medicine.basePrice ?? ""}
                                            unitPrice={medicine.unitPrice ?? ""}
                                            onOpen={() => {
                                                // 상세의 키워드 목록에서 다른 약품 상세로 여는 클릭
                                                if (!medicine.id || !medicineId || selectedKeywords.length === 0) return;

                                                createMedicineEntryLog({
                                                    source: "KEYWORD_LIST",
                                                    surface: "MEDICINE",
                                                    medicineId: medicine.id,
                                                    fromMedicineId: medicineId,
                                                    keywords: selectedKeywords,
                                                });
                                            }}
                                        />
                                ))
                            )}

                            {hasNextMedicinesByKeywordsPage && (
                                <Button 
                                    variant={"ghost"} 
                                    onClick={() => fetchNextMedicinesByKeywords()} 
                                    isLoading={isFetchingNextMedicinesByKeywordsPage}
                                    
                                    size={"xs"}
                                    marginLeft={16}
                                >
                                    더보기
                                </Button>
                            )}

                            {(!isMedicinesByKeywordsDataLoading && medicinesByKeywordsData && medicinesByKeywordsData.pages.flatMap((page) => page.medicines).length === 0) && (
                                <HStack
                                    width={"100%"}
                                    minHeight={"220px"}
                                    spacing={4}
                                    paddingY={5}
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                >
                                    <Text fontSize={14} fontWeight={500} color={colors.g3}>No Medicines</Text>
                                </HStack>
                            )}
                        </HStack>
                        
                    </Stack>
                }

                {/* 조회수 영역 */}

                <HStack width={"100%"} justifyContent={"flex-end"} alignItems={"center"} marginTop={10} paddingX={5}>
                    <Icon as={ViewIcon} color={colors.g3} width={5} height={5} />
                    <Text fontSize={14} fontWeight={500} color={colors.g3}>{medicineData?.viewCount ?? 0}</Text>
                </HStack>

                {/* 탭 */}

                <Tabs isFitted marginTop={16} width={"100%"}>
                    <TabList>
                        <Tab _selected={{color: colors.blue, fontWeight: 700, borderBottom: `2px solid ${colors.blue}`}} padding={3} fontSize={16}>기본정보</Tab>
                        <Tab _selected={{color: colors.blue, fontWeight: 700, borderBottom: `2px solid ${colors.blue}`}} padding={3} fontSize={16}>추가정보</Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel padding={10}>
                            <Text fontSize={18} fontWeight={700} color={colors.g1} marginTop={10}>구분</Text>
                            <Text fontSize={16} fontWeight={500} color={colors.g1} marginTop={5}>{medicineData?.medicine.category ?? "-"}</Text>
                            <Text fontSize={18} fontWeight={700} color={colors.g1} marginTop={10}>식약처분류</Text>
                            <Text fontSize={16} fontWeight={500} color={colors.g1} marginTop={5}>{medicineData?.medicine.classification ?? "-"}</Text>
                            <Text fontSize={18} fontWeight={700} color={colors.g1} marginTop={10}>성분/함량</Text>
                            <Text fontSize={16} fontWeight={500} color={colors.g1} marginTop={5}>{medicineData?.medicine.ingredientInfo ?? "-"}</Text>
                            <Text fontSize={18} fontWeight={700} color={colors.g1} marginTop={10}>업체명</Text>
                            <Text fontSize={16} fontWeight={500} color={colors.g1} marginTop={5}>{medicineData?.medicine.seller ?? "-"}</Text>
                            <Text fontSize={18} fontWeight={700} color={colors.g1} marginTop={10}>효능효과</Text>
                            <Text fontSize={16} fontWeight={500} color={colors.g1} marginTop={5}>{medicineData?.medicine.efficacy ?? "-"}</Text>
                            <Text fontSize={18} fontWeight={700} color={colors.g1} marginTop={10}>용법용량</Text>
                            <Text fontSize={16} fontWeight={500} color={colors.g1} marginTop={5}>{medicineData?.medicine.usage ?? "-"}</Text>
                        </TabPanel>
                        <TabPanel padding={10}>
                            <Text fontSize={18} fontWeight={700} color={colors.g1} marginTop={10}>비고</Text>
                            <Text fontSize={16} fontWeight={500} color={colors.g1} marginTop={5}>{medicineData?.medicine.note ?? "-"}</Text>
                        </TabPanel>
                    </TabPanels>
                </Tabs>

                {mallHref && (
                    <Button
                        position="fixed"
                        bottom="5%"
                        left="50%"
                        transform="translate(-50%, -50%)"
                        zIndex="1000"
                        border="1px solid"
                        borderColor={colors.link}
                        borderRadius={30}
                        backgroundColor={colors.white}
                        color={colors.link}
                        paddingX={10}
                        paddingY={5}
                        onClick={() => {
                            // 온라인몰로 이동하는 클릭을 기록한다
                            if (medicineId) {
                                createClickLog({
                                    type: "MALL",
                                    medicineId,
                                });
                            }
                            window.open(mallHref, "_blank", "noopener,noreferrer");
                        }}
                    >
                        <HStack>
                            <Text fontSize={16} fontWeight={700} color={colors.link}>
                                온라인몰 바로가기
                            </Text>
                        </HStack>
                    </Button>
                )}

            </Template>
            <Alert {...alertProps} />
        </LogInOnly>
    );
}